import { PlayPauseData } from "../../../types/rootDataTypes";
import { VideoState } from "../../video-player-integration/video-player";
import { getCurrentAudioTime } from "./getCurrentAudioTime";
import { getNearestPlayPoint } from "./getNearestPlayPoint";
import { getTimelineSegmentIndex } from "./getTimelineSegmentIndex";

type State = {
  videoTime: number;
  audioTime: number;
  pausePointSegments: number[];
  playPointSegments: number[];
  extended: PlayPauseData[];

  // These state values could change outside of the state machine
  isAdEnabled: boolean;
  videoState: VideoState;
  videoSeekPosition: number | null;
};

type Actions = {
  playAudio: () => void;
  pauseAudio: () => void;
  seekAudioTo: (to: number) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  resetVideoSeekPosition: () => void;
};

export type StateFn = (state: State, actions: Actions) => StateFn;

// State Function naming convention: relevant state values in this order separated by underscores
// - AD can be on or off -> `ADon` | `ADoff`
// - the video can be playing, paused, or buffering -> `Vl` | `Vu` | `Vb`
// - the audio can be playing or paused -> `Al` | `Au`
// - we could be in an EAD pause -> `EADon` | `EADoff`

// IMPORTANT NOTE WHEN WRITING THESE STATE FUNCTIONS:
// As noted above, these three `state` properties could change outside of the state machine loop:
// 1. `isAdEnabled`
// 2. `videoState`
// 3. `videoSeekPosition`
// MAKE SURE you handle these values changing FIRST in each state function.

/**
 * While AD is off, the video player can be in any state and the audio will be paused.
 */
export const ADoff_Vx_Au_EADoff: StateFn = (state, actions) => {
  if (state.isAdEnabled) {
    syncAudio(state, actions);

    switch (state.videoState) {
      case "playing":
        actions.playAudio();
        return ADon_Vl_Al_EADoff;

      case "paused":
        return ADon_Vu_Au_EADoff;

      case "buffering":
        return ADon_Vb_Au_EADoff;
    }
  }

  return ADoff_Vx_Au_EADoff;
};

const ADon_Vu_Au_EADoff: StateFn = (state, actions) => {
  if (!state.isAdEnabled) {
    return ADoff_Vx_Au_EADoff;
  }

  if (state.videoState === "playing") {
    syncAudio(state, actions);
    actions.playAudio();
    return ADon_Vl_Al_EADoff;
  }

  if (state.videoState === "buffering") {
    return ADon_Vb_Au_EADoff;
  }

  if (state.videoSeekPosition !== null) {
    syncAudio(state, actions);
    return ADon_Vu_Au_EADoff;
  }

  return ADon_Vu_Au_EADoff;
};

const ADon_Vu_Al_EADon: StateFn = (state, actions) => {
  if (!state.isAdEnabled) {
    actions.pauseAudio();
    actions.playVideo();
    return ADoff_Vx_Au_EADoff;
  }

  if (state.videoState === "playing") {
    syncAudio(state, actions);
    return ADon_Vl_Al_EADoff;
  }

  if (state.videoState === "buffering") {
    // if we start buffering during an EAD pause, we will allow the audio to continue playing and
    // use the next StateFn to handle when we reach the end of the EAD pause.
    return ADon_Vb_Al_EADon;
  }

  if (state.videoSeekPosition !== null) {
    syncAudio(state, actions);
    actions.playVideo();
    return ADon_Vl_Al_EADoff;
  }

  if (isOnPlayPoint(state)) {
    actions.playVideo();
    return ADon_Vl_Al_EADoff;
  }

  return ADon_Vu_Al_EADon;
};

const ADon_Vl_Al_EADoff: StateFn = (state, actions) => {
  if (!state.isAdEnabled) {
    actions.pauseAudio();
    return ADoff_Vx_Au_EADoff;
  }

  if (state.videoState === "paused") {
    actions.pauseAudio();
    return ADon_Vu_Au_EADoff;
  }

  if (state.videoState === "buffering") {
    actions.pauseAudio();
    return ADon_Vb_Au_EADoff;
  }

  if (state.videoSeekPosition !== null) {
    syncAudio(state, actions);
    return ADon_Vl_Al_EADoff;
  }

  if (isOnPausePoint(state) && !isOnPlayPoint(state)) {
    actions.pauseVideo();
    return ADon_Vu_Al_EADon;
  }

  return ADon_Vl_Al_EADoff;
};

const ADon_Vb_Au_EADoff: StateFn = (state, actions) => {
  if (!state.isAdEnabled) {
    return ADoff_Vx_Au_EADoff;
  }

  if (state.videoState === "playing") {
    syncAudio(state, actions);
    actions.playAudio();
    return ADon_Vl_Al_EADoff;
  }

  if (state.videoState === "paused") {
    return ADon_Vu_Au_EADoff;
  }

  if (state.videoSeekPosition !== null) {
    syncAudio(state, actions);
    return ADon_Vb_Au_EADoff;
  }

  return ADon_Vb_Au_EADoff;
};

/**
 * We will get to this state when we reach the play point at the end of the EAD pause but the video
 * has not finished buffering. Just wait here forever until the stateFn is updated when the video
 * stops buffering (or some other reason).
 */
const ADon_Vb_Au_EADon: StateFn = (state, actions) => {
  if (!state.isAdEnabled) {
    return ADoff_Vx_Au_EADoff;
  }

  if (state.videoState === "playing") {
    actions.playAudio();
    return ADon_Vl_Al_EADoff;
  }

  if (state.videoState === "paused") {
    actions.playVideo();
    actions.playAudio();
    return ADon_Vl_Al_EADoff;
  }

  if (state.videoSeekPosition !== null) {
    syncAudio(state, actions);
    return ADon_Vb_Au_EADoff;
  }

  return ADon_Vb_Au_EADon;
};

/**
 * If the video buffers while we are in an EAD pause, we will let the audio keep playing until we
 * reach the play point.
 */
const ADon_Vb_Al_EADon: StateFn = (state, actions) => {
  if (!state.isAdEnabled) {
    actions.pauseAudio();
    return ADoff_Vx_Au_EADoff;
  }

  if (state.videoState === "playing") {
    console.warn(
      "during EAD pause, video switch from buffering to playing -- this is unexpected"
    );
    syncAudio(state, actions);
    return ADon_Vl_Al_EADoff;
  }

  if (state.videoState === "paused") {
    return ADon_Vu_Al_EADon;
  }

  if (state.videoSeekPosition !== null) {
    syncAudio(state, actions);
    actions.playVideo();
    return ADon_Vl_Al_EADoff;
  }

  if (isOnPlayPoint(state)) {
    actions.pauseAudio();
    return ADon_Vb_Au_EADon;
  }

  return ADon_Vb_Al_EADon;
};

function isOnPausePoint(state: State) {
  const currentVideoSegment = getTimelineSegmentIndex(state.videoTime * 1000);

  // Sometimes we can miss a pause point because of some jitter with timers, so we check the current
  // segment as well as the previous segment.
  return (
    state.pausePointSegments.includes(currentVideoSegment) ||
    state.pausePointSegments.includes(currentVideoSegment - 1)
  );
}

function isOnPlayPoint(state: State) {
  const currentAudioSegment = getTimelineSegmentIndex(state.audioTime * 1000);

  // Sometimes we can miss a play point because of some jitter with timers, so we check the current
  // segment as well as a few previous segments.
  //
  // IMPORTANT: It is critical that we check more segments here than we do for pause points to
  // create a buffer of time to transition from being on both a pause point and play point at the
  // same time to being only on the play point. Otherwise the state machine might think we reached
  // a pause point again as soon as we try to exit the EAD pause.
  return (
    state.playPointSegments.includes(currentAudioSegment) ||
    state.playPointSegments.includes(currentAudioSegment - 1) ||
    state.playPointSegments.includes(currentAudioSegment - 2) ||
    state.playPointSegments.includes(currentAudioSegment - 3) ||
    state.playPointSegments.includes(currentAudioSegment - 4)
  );
}

function syncAudio(state: State, actions: Actions) {
  // If we happen to be directly on a pause point, then we will skip ahead to the corresponding play
  // point and pick up from _after_ the EAD pause.
  actions.seekAudioTo(
    isOnPausePoint(state)
      ? getNearestPlayPoint({
          currentVideoTime: state.videoTime,
          extended: state.extended,
        })
      : getCurrentAudioTime({
          currentVideoTime: state.videoTime,
          extended: state.extended,
        })
  );

  actions.resetVideoSeekPosition();
}
